"use client";
import { languageDestination } from "@/lib/i18n/navigation";
import { firstBatchPreviewPublicPath } from "@/lib/i18n/first-batch-preview";
import { PILOT_CONTENT_REGISTRY } from "@/lib/i18n/content-registry";

import Image from "next/image";
import publicCopy from "@/messages/mandarin-public.json";
import Link from "next/link";
import type { Route } from "next";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import LogoBlue from "@/app/(assets)/logo_rectangle_blue_highres.png";
import LogoWhite from "@/app/(assets)/logo_rectangle_white_highres.png";
import WhatsAppButton from "@/app/components/WhatsAppButton";
import { headerNavLinkClass } from "@/app/components/header-nav-link";
import { getPilotFacts } from "@/lib/i18n/mandarin-pilot";
import {
  ENGLISH_JOURNEY_PATHS,
  PUBLIC_MANDARIN_JOURNEY_PATHS,
} from "@/lib/i18n/publication";

const copy = publicCopy.chrome;

const MIST_BACKGROUND_SRC = "/optimised/mist-20251104-0718a1b6.jpg";
const enquiryMessage = copy.enquiry;

function publicPathname(pathname: string) {
  pathname = firstBatchPreviewPublicPath(pathname);
  return pathname === "/zh-Hans" || pathname.startsWith("/zh-Hans/")
    ? `/zh${pathname.slice("/zh-Hans".length)}`
    : pathname;
}

function PilotLanguageControl({ tone }: { tone: "light" | "dark" }) {
  const pathname = publicPathname(usePathname());
  const counterpart = PILOT_CONTENT_REGISTRY.resolveCounterpart(pathname, "en");
  const englishHref =
    counterpart.kind === "available"
      ? counterpart.href
      : counterpart.kind === "unavailable"
      ? counterpart.englishPath
      : ENGLISH_JOURNEY_PATHS.home;
  const currentClass = tone === "dark" ? "text-white" : "text-blue-950";
  const linkClass =
    tone === "dark"
      ? "text-white/90 underline underline-offset-4 hover:text-white"
      : "text-blue-800 underline underline-offset-4 hover:text-blue-950";
  return (
    <div
      aria-label={copy.languageLabel}
      className="flex min-h-11 items-center gap-2 px-2 text-sm font-semibold"
    >
      <span lang="zh-Hans" aria-current="true" className={currentClass}>
        {copy.chineseLabel}
      </span>
      <span
        aria-hidden="true"
        className={tone === "dark" ? "text-white/50" : "text-slate-400"}
      >
        /
      </span>
      <Link<string> href={englishHref as Route} hrefLang="en" lang="en" className={linkClass}>
        English
      </Link>
    </div>
  );
}

function PilotNav({
  tone,
  action,
}: {
  tone: "light" | "dark";
  action: "header" | "landing_header";
}) {
  const t = useTranslations("pilot");
  const pathname = publicPathname(usePathname());
  const labels = [t("navHome"), t("navCourse"), t("navVisit")];
  return (
    <>
      {[
        PUBLIC_MANDARIN_JOURNEY_PATHS.home,
        PUBLIC_MANDARIN_JOURNEY_PATHS.course,
        PUBLIC_MANDARIN_JOURNEY_PATHS.visit,
      ].map((href, index) => (
        <Link
          key={href}
          href={href}
          aria-current={pathname === href ? "page" : undefined}
          data-ga-event="primary_nav_click"
          data-ga-category="navigation"
          data-ga-action={action}
          data-ga-label={`mandarin_pilot_${index}`}
          className={headerNavLinkClass({ tone, isOpen: pathname === href })}
        >
          {labels[index]}
        </Link>
      ))}
    </>
  );
}

export function PublicMandarinHomeHeader() {
  const t = useTranslations("pilot");
  return (
    <header className="relative z-40 overflow-visible border-b border-blue-100 text-black">
      <div className="pointer-events-none absolute inset-0">
        <Image
          src={MIST_BACKGROUND_SRC}
          alt=""
          fill
          className="object-cover"
          sizes="100vw"
          priority
        />
      </div>
      <div
        data-home-header-tint
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/95 via-blue-50/90 to-indigo-50/85"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-6xl px-4 py-3 sm:py-4 xl:max-w-7xl">
        <div className="flex flex-col items-center gap-2 md:flex-row md:justify-between md:gap-0">
          <Link
            href={PUBLIC_MANDARIN_JOURNEY_PATHS.home}
            className="rounded focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300/70"
          >
            <Image
              src={LogoBlue}
              width={180}
              height={48}
              alt="Eclat Institute"
              className="mb-1 h-12 w-auto object-contain mix-blend-multiply sm:h-14 md:mb-0 md:mr-4 md:h-16"
              priority
            />
          </Link>
          <nav
            aria-label={t("navLabel")}
            className="flex flex-wrap items-center justify-center gap-0.5 gap-y-2 md:gap-2 lg:gap-4 md:justify-start"
          >
            <PilotNav tone="light" action="landing_header" />
            <WhatsAppButton
              placement="header"
              label={t("ask")}
              message={enquiryMessage}
              compact
            />
            <PilotLanguageControl tone="light" />
          </nav>
        </div>
        <Link<string>
          href={languageDestination("/blog", "zh-Hans") as Route}
          hrefLang="zh-Hans"
          className="mt-4 flex min-h-12 w-full items-center rounded-full border border-blue-200 bg-white/80 px-5 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-blue-400 hover:text-blue-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300/60 md:mt-5"
        >
          {t("resources")}{" "}
          <span className="ml-auto text-blue-700" aria-hidden="true">
            ↗
          </span>
        </Link>
      </div>
    </header>
  );
}

export function PublicMandarinEditorialHeader() {
  const t = useTranslations("pilot");
  return (
    <header
      data-site-header
      data-site-header-surface="editorial-blue"
      className="border-b border-white/20 bg-[radial-gradient(circle_at_72%_0%,rgba(149,197,235,0.24),transparent_46%),linear-gradient(110deg,#315f96_0%,#2d67aa_58%,#3978b8_100%)] text-white"
    >
      <div
        data-site-header-inner
        className="mx-auto flex max-w-5xl xl:max-w-7xl flex-col items-center gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between md:gap-0"
      >
        <Link
          href={PUBLIC_MANDARIN_JOURNEY_PATHS.home}
          data-site-header-logo
          className="flex min-h-12 items-center gap-2 rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-600"
        >
          <Image
            src={LogoWhite}
            alt="Eclat Institute"
            className="mx-auto h-10 w-auto rounded-full object-cover md:mx-0 md:h-12"
            sizes="(max-width: 480px) 140px, (max-width: 1024px) 160px, 200px"
            quality={85}
            priority
          />
        </Link>
        <nav
          aria-label={t("navLabel")}
          data-site-header-primary
          className="mt-2 flex w-full flex-wrap items-center justify-center gap-1 gap-y-2 md:gap-2 lg:gap-4 md:mt-0 md:w-auto md:justify-end"
        >
          <PilotNav tone="dark" action="header" />
          <WhatsAppButton
            placement="header"
            label={t("ask")}
            message={enquiryMessage}
            compact
          />
          <PilotLanguageControl tone="dark" />
        </nav>
      </div>
    </header>
  );
}

const footerLink =
  "block leading-snug text-white/90 underline underline-offset-2 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";

export function PublicMandarinFooter() {
  const t = useTranslations("pilot");
  const consent = useTranslations("cookieConsent");
  const facts = getPilotFacts();
  const groups = [
    {
      heading: copy.coursesHeading,
      links: [
        [PUBLIC_MANDARIN_JOURNEY_PATHS.home, t("navHome")],
        [PUBLIC_MANDARIN_JOURNEY_PATHS.course, t("navCourse")],
        [PUBLIC_MANDARIN_JOURNEY_PATHS.visit, t("navVisit")],
      ],
    },
    {
      heading: copy.resourcesHeading,
      links: [
        [languageDestination("/blog", "zh-Hans"), t("resources")],
        [languageDestination("/tuition", "zh-Hans"), copy.allCourses],
      ],
    },
    {
      heading: "Eclat Institute",
      links: [
        [languageDestination("/tutors", "zh-Hans"), copy.tutorsLink],
        ["https://eclatinstitute.sg/legal", t("legal")],
      ],
    },
    {
      heading: copy.languageHeading,
      links: [
        [PUBLIC_MANDARIN_JOURNEY_PATHS.home, copy.chineseLabel],
        ["https://eclatinstitute.sg/", "English"],
      ],
    },
  ] as const;
  return (
    <footer
      data-mandarin-footer
      data-nosnippet
      className="relative overflow-hidden bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 py-10 text-white"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "image-set(url('/optimised/footer-noise-20251104-1f5d7d56.avif') type('image/avif') 1x, url('/optimised/footer-noise-20251104-1f5d7d56.webp') type('image/webp') 1x)",
          backgroundSize: "128px 128px",
          backgroundRepeat: "repeat",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-blue-950/60"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-stretch">
          <section
            className="order-1 h-full rounded-2xl border border-white/15 bg-white/5 p-5 shadow-[0_18px_40px_rgba(0,0,0,0.18)] backdrop-blur-sm md:p-6"
            aria-label={copy.centreHeading}
          >
            <p className="text-xl font-bold tracking-normal text-white md:text-2xl">
              {copy.centreHeading}
            </p>
            <address className="mt-4 not-italic text-sm leading-7 text-white/90">
              {facts.centreAddress.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </address>
            <p className="mt-4 text-sm leading-6 text-white/75">
              {copy.venueReminder}
            </p>
          </section>
          <section
            className="order-2 h-full rounded-2xl border border-white/15 bg-white/5 p-5 text-left shadow-[0_18px_40px_rgba(0,0,0,0.18)] backdrop-blur-sm md:p-6"
            aria-label={copy.labLabel}
          >
            <p className="text-xl font-bold tracking-normal text-white md:text-2xl">
              {copy.labHeading}
            </p>
            <address className="mt-4 not-italic text-sm leading-7 text-white/90">
              {facts.labAddress.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </address>
            <Link
              href={PUBLIC_MANDARIN_JOURNEY_PATHS.visit}
              className={`${footerLink} mt-4 font-semibold`}
            >
              {copy.visitLink}
            </Link>
          </section>
          <div className="order-3 text-center lg:col-span-2 lg:border-t lg:border-white/15 lg:pt-8">
            <nav
              className="mx-auto grid w-full max-w-3xl grid-cols-2 gap-x-8 gap-y-6 text-sm md:grid-cols-4"
              aria-label={copy.footerLabel}
            >
              {groups.map((group) => (
                <div key={group.heading} className="space-y-2 text-left">
                  <p className="text-sm font-semibold text-white">
                    {group.heading}
                  </p>
                  <div className="space-y-2">
                    {group.links.map(([href, label]) => (
                      <Link<string> key={href} href={href as Route} className={footerLink}>
                        {label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(new CustomEvent("open-cookie-prefs"))
              }
              className={`${footerLink} mx-auto mt-8`}
            >
              {consent("title")}
            </button>
            <p className="mt-8">{copy.copyright}</p>
            <p className="mt-2 text-white/80">{t("footer")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
export function PublicMandarinBreadcrumb({
  current,
}: {
  current: "course" | "visit";
}) {
  const t = useTranslations("pilot");
  const label = current === "course" ? t("navCourse") : t("navVisit");
  return (
    <nav
      aria-label={copy.breadcrumbLabel}
      data-breadcrumb-trail
      className="sticky top-0 z-30 mb-6 border-b border-blue-100/60 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80"
    >
      <ol className="mx-auto flex max-w-5xl flex-wrap items-center gap-2 px-4 py-2 text-xs font-medium text-blue-800 sm:text-sm">
        <li className="flex items-center gap-2">
          <Link
            href={PUBLIC_MANDARIN_JOURNEY_PATHS.home}
            className="inline-flex items-center rounded-full bg-white px-3 py-1 text-blue-700 shadow-sm ring-1 ring-blue-100 transition hover:bg-blue-50 hover:text-blue-900"
          >
            {t("navHome")}
          </Link>
        </li>
        <li className="flex items-center gap-2">
          <span aria-hidden className="text-blue-300">
            ›
          </span>
          <span
            aria-current="page"
            className="inline-flex items-center rounded-full bg-blue-600/10 px-3 py-1 font-semibold text-blue-900"
          >
            {label}
          </span>
        </li>
      </ol>
    </nav>
  );
}
